import moment from 'moment';
import { isSandals } from 'js/modules/brand';
const gatracking = {};

/**
 * returns the number of nights base on the check in and check out date
 * @param {String} checkInDate
 * @param {String} checkOutDate
 * @returns {Number} number of nights
 */
const calculateNights = (checkInDate, checkOutDate) => {
  const ckIn = new Date(checkInDate);
  const ckOut = new Date(checkOutDate);
  const _mCkIn = moment(ckIn);
  const _mCkOut = moment(ckOut);
  const numberOfNights = _mCkOut.diff(_mCkIn, 'days');

  return numberOfNights;
};

/**
 * returns mixed string with the promotions and discounts of a room reservation
 * @param {Array} promotions
 * @param {Array} discounts
 * @returns {String} promotions and discounts
 */
const getPromotionAndDiscounts = (promotions, discounts) => {
  const promosAndDiscounts = [];

  promotions.forEach(promo => {
    promosAndDiscounts.push(promo.promotionDescription);
  });

  discounts.forEach(discount => {
    promosAndDiscounts.push(discount.description);
  });

  return promosAndDiscounts.join(', ');
};

/**
 * returns the category full name base on the room class and attributes of the room
 * @param {String} roomClass
 * @param {Array} attributes
 * @returns {String} category full name
 */
const getRoomItemCategory = (roomClass, attributes) => {
  let category = '';

  switch (roomClass) {
    case 'BUTLER':

      if(attributes.length > 0) {
        const isLoveNest = attributes.some((attribute) => attribute.id === 7);
        const nestType = isSandals ? 'Love Nest' : 'Together Nest';

        category = isLoveNest ? nestType : 'Butler Elite';
      }
      break;

    case 'LUXURY':
      category = isSandals ? 'Sandals Luxury' : 'Luxury';
      break;

    case 'CONCIERGE':
      category = isSandals ? 'Club Sandals' : 'Concierge';
      break;

    default:
      category = isSandals ? 'Sandals Luxury' : 'Luxury';
      break;
  }

  return category;
};

/**
 returns a gtm event depending of the step
 * @param {String} eventName
 * @param {Number} step
 * @param {Object} vacationSummary
 * @returns {Object} event
 */
const createLayer = (eventName, step, vacationSummary) => {
  const { webConfirmationNumber } = vacationSummary;
  const affiliateInfo = vacationSummary.affiliateInfo ? vacationSummary.affiliateInfo : '';
  const utmMedium = affiliateInfo.utm_medium ? affiliateInfo.utm_medium : '';

  const stepEvents = {
    event: eventName,
    ecommerce: {
      affiliation: utmMedium,
      items: [{
        item_id: vacationSummary.resortInfo.rstCode,
        item_name: '',
        item_category: '',
        price: '',
        promotion_name: '',
        quantity: 1,
      }]
    }
  };

  if(step > 2) {
    const promos = getPromotionAndDiscounts(vacationSummary.roomData.promotions, vacationSummary.roomData.discounts);

    stepEvents.ecommerce.items[0].item_name = vacationSummary.roomData.roomName;
    stepEvents.ecommerce.items[0].price = parseFloat(vacationSummary.roomData.afterPromoRate.toFixed(2));
    stepEvents.ecommerce.items[0].item_category = getRoomItemCategory(vacationSummary.roomData.roomClass, vacationSummary.roomData.attributes);
    stepEvents.ecommerce.items[0].promotion_name = promos;
  }

  if(step > 3) {
    stepEvents.ecommerce.currency = 'USD';
  }

  if(step > 5) {
    stepEvents.ecommerce.value = parseFloat(vacationSummary.transaction.grandTotal.toFixed(2));
    stepEvents.ecommerce.transaction_id = webConfirmationNumber;
  }

  if(step === 6) {
    stepEvents.ecommerce.email = vacationSummary.guestsData.email;
  }

  return stepEvents;
};

const getSoldOuts = (roomsSummary) => {
  const rooms = roomsSummary.rooms ? roomsSummary.rooms : [];

  const available = rooms.filter((room) => room.available);
  const soldOutTotal =  rooms.length - available.length;

  return soldOutTotal;
};

/**
 * returns a gtm event with all the information needed for the select_item event
 * @param {Object} roomData
 * @param {Object} vacationSummary
 * @returns {Object} event
 */
gatracking.createLayerForRooms = (roomData, vacationSummary) => {
  const affiliateInfo = vacationSummary.affiliateInfo ? vacationSummary.affiliateInfo : '';
  const utmMedium = affiliateInfo.utm_medium ? affiliateInfo.utm_medium : '';
  const promos = getPromotionAndDiscounts(roomData.promotions, roomData.discounts);

  const stepEvents = {
    event: 'select_item',
    ecommerce: {
      affiliation: utmMedium,
      items: [{
        item_id: vacationSummary.resortInfo.rstCode,
        item_name: roomData.roomName,
        item_category: getRoomItemCategory(roomData.roomClass, roomData.attributes),
        price: parseFloat(roomData.afterPromoRate.toFixed(2)),
        promotion_name: promos,
        quantity: 1,
      }]
    }
  };

  return stepEvents;
};

/**
 * only for Room step
 * @param {Object} vacationSummary 
 * @param {Object} roomsSummary
 */
gatracking.createStep2DataLayer = (vacationSummary, roomsSummary) => {
  const {
    vacationData = {}
  } = vacationSummary;

  const stepdata = {
    event: 'stepdata',
    adults: 0,
    checkin: '',
    checkout: '',
    number_of_nights: '',
    flight_price: 0,
    flight_departing: '',
    step_number: 2,
    room_code: '',
    resort_code: '',
    location: '',
    cobrand: '',
    discount_value: 0,
    payment_method: '',
    insurance_price: 0,
    custom_amount: 0,
    room_category: '',
  };

  const {
    checkInDate,
    checkOutDate,
    adults,
    children,
    infants,
  } = vacationData;

  const checkIn = moment(checkInDate).format('MM/DD/YYYY').toString();
  const checkOut = moment(checkOutDate).format('MM/DD/YYYY').toString();
  const rstCode = vacationSummary.resortInfo.rstCode;

  stepdata.checkin = checkIn;
  stepdata.checkout = checkOut;
  stepdata.resort_code = rstCode;

  stepdata.adults = adults;
  stepdata.location = vacationSummary.resortInfo.city + ' - ' + vacationSummary.resortInfo.country;
  stepdata.number_of_nights = calculateNights(checkInDate, checkOutDate);

  if(!isSandals) {
    stepdata.children = children;
    stepdata.infants = infants;
  }

  stepdata.sold_out = getSoldOuts(roomsSummary);

  if (typeof dataLayer !== 'undefined') {
    dataLayer.push(stepdata);
  }
};

/** 
 * only for Vacation step
 * @param {*} vacationSummary 
 */
gatracking.createStep1DataLayer = (rstCode, checkInDate, checkOutDate) => {

  const stepdata = {
    event: 'stepdata',
    adults: 0,
    checkin: '',
    checkout: '',
    number_of_nights: '',
    flight_price: 0,
    flight_departing: '',
    step_number: 1,
    room_code: '',
    resort_code: '',
    location: '',
    cobrand: '',
    discount_value: 0,
    payment_method: '',
    insurance_price: 0,
    custom_amount: 0,
    room_category: '',
  };

  const checkIn = moment(checkInDate).format('MM/DD/YYYY').toString();
  const checkOut = moment(checkOutDate).format('MM/DD/YYYY').toString();

  stepdata.checkin = checkIn;
  stepdata.checkout = checkOut;
  stepdata.resort_code = rstCode.toUpperCase();

  if (typeof dataLayer !== 'undefined') {
    dataLayer.push(stepdata);
  }
};

/**
 * returns a gtm event with all the information needed for the stepdata event
 * @param {Object} vacationSummary
 * @param {Number} step
 * @returns {Object} event
 */
gatracking.getStepDataLayerInfo = (vacationSummary, step) => {
  const {
    vacationData = {}
  } = vacationSummary;

  const stepdata = {
    event: 'stepdata',
    adults: 0,
    checkin: '',
    checkout: '',
    number_of_nights: '',
    flight_price: 0,
    flight_departing: '',
    step_number: step,
    room_code: '',
    resort_code: '',
    location: '',
    cobrand: '',
    discount_value: 0,
    payment_method: '',
    insurance_price: 0,
    custom_amount: 0,
    room_category: '',
  };

  if (step >= 1) {
    const {
      checkInDate,
      checkOutDate,
      airIncluded,
      adults,
      children,
      infants,
    } = vacationData;

    if(step > 2) {
      const checkIn = moment(checkInDate).format('MM/DD/YYYY').toString();
      const checkOut = moment(checkOutDate).format('MM/DD/YYYY').toString();
      const rstCode = vacationSummary.resortInfo.rstCode;

      stepdata.adults = adults;
      stepdata.checkin = checkIn;
      stepdata.checkout = checkOut;
      stepdata.resort_code = rstCode;
      stepdata.location = vacationSummary.resortInfo.city + ' - ' + vacationSummary.resortInfo.country;
      stepdata.number_of_nights = calculateNights(checkInDate, checkOutDate);
      stepdata.room_code = vacationSummary.roomData.roomCode;
      stepdata.room_category = getRoomItemCategory(vacationSummary.roomData.roomClass, vacationSummary.roomData.attributes);

      if(!isSandals) {
        stepdata.children = children;
        stepdata.infants = infants;
      }
    }

    if(step > 3) {
      stepdata.flight_price = airIncluded ? vacationSummary.flightsData.priceInfo.grandTotalAmount : 0;
      stepdata.flight_departing = airIncluded ? vacationSummary.selectedAirline : '';
    }

    if(step === 6 ) {
      const cobrandData = vacationSummary.cobrandInfo ? vacationSummary.cobrandInfo : '';

      stepdata.payment_method = vacationSummary.paymentOption === 'uplift' ? vacationSummary.paymentOption : vacationSummary.transaction.typeOfPayment;
      stepdata.cobrand = cobrandData ? cobrandData.agency : '';
      stepdata.insurance_price = vacationSummary.travelProtection ? vacationSummary.travelProtection[0].amount : 0;
      stepdata.discount_value = vacationSummary.ssgMember ? vacationSummary.ssgMember.pointDiscount : 0;
      stepdata.custom_amount = vacationSummary.transaction.grandTotalPaid;
    }
  }

  return stepdata;
};

/**
 * returns a gtm event with all the information needed event by step
 * @param {Object} vacationSummary
 * @param {String} brand
 * @param {Number} step
 * @returns {Object} event
 */
gatracking.getLayerInfoByStep = (vacationSummary, brand, step) => {
  let stepLayer = {};

  if(step === 4) {
    stepLayer = createLayer('begin_checkout', step, vacationSummary);
  }
  if(step === 5) {
    stepLayer = createLayer('add_payment_info', step, vacationSummary);
  }
  if(step === 6) {
    stepLayer = createLayer('purchase', step, vacationSummary);
  }

  return stepLayer;
};

/**
 * tracker only for the room step, its execute when the client select a room, pushed to the gtm dataLayer.
 * you will find the call for this method in the submitRoom method in the RoomContext.js
 * @param {Object} roomData
 * @param {Object} vacationSummary
 */
gatracking.trackRoom = (roomData, vacationSummary) => {
  const layerForRooms = gatracking.createLayerForRooms(roomData, vacationSummary);

  if (typeof dataLayer !== 'undefined') {
    dataLayer.push(layerForRooms);
  }
};

/**  **/
/**
 * tracker for all the steps when each steps loads, and pushed to the gtm dataLayer
 * @param {Object} vacationSummary
 * @param {String} brand
 * @param {Object} step
 */
gatracking.track = (vacationSummary, brand, step) => {
  const stepdataLayerInfo = gatracking.getStepDataLayerInfo(vacationSummary, step);
  const layerInfo = gatracking.getLayerInfoByStep(vacationSummary, brand, step);
  const entries = performance.getEntriesByType('navigation');

  entries.forEach((entry) => {
    if(entry.type === 'navigate') {
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push(stepdataLayerInfo);
        if(step !== 1 && step !== 2 && step !== 3) {
          dataLayer.push(layerInfo);
        }
      }
    }
  });
};

export default gatracking;



// WEBPACK FOOTER //
// ./js/modules/gatracking.js